import { useNavigate } from "react-router-dom";

import { cn } from "../lib/utils";
import coinMarketCap from "../assets/images/assetdashboard/coinMarketCap.png";
import {
  formatFiatWithTwoDecimals,
  formatWithSignificantDigits,
  parseNumericValue,
} from "../utils/numberFormatter";
import {
  formatPercentOfATH,
  formatMarketCap,
  formatSupply,
  formatVolume,
} from "../utils/marketDataFormatter";
import { EMPTY_YIELD_MARKET_DATA } from "../utils/zar8Yield";

const FALLBACK_DESCRIPTION =
  "ZAR8 is a rand-backed digital asset designed to keep a stable reference to the South African rand while unlocking access to crypto yield opportunities.";

const CTA_ARROW_ICON = "/figma/zar8-yield/cta-arrow.svg";
const TRACK_ICON = "/figma/zar8-yield/track-icon.svg";
const CALCULATOR_ICON = "/figma/zar8-yield/calculator-icon.svg";
const UNLOCK_ILLUSTRATION = "/figma/zar8-yield/unlock-illustration.svg";
const HISTORY_ILLUSTRATION = "/figma/zar8-yield/history-illustration.svg";
const MANAGE_ICON_PART = "/figma/zar8-yield/manage-icon-part-1.svg";
const HERO_BACKGROUND = "/figma/zar8-yield/hero-background.svg";
const BALANCE_BACKGROUND = "/figma/zar8-yield/balance-background.svg";
const NOISE_TEXTURE = "/figma/zar8-yield/noise.png";
const HERO_BASE_GRADIENT =
  "linear-gradient(90deg, rgba(87, 36, 156, 0.5) 0%, rgba(87, 36, 156, 0.5) 100%), linear-gradient(90deg, rgb(95, 117, 255) 0%, rgb(151, 71, 255) 100%)";
const BALANCE_BASE_GRADIENT =
  "linear-gradient(180deg, rgba(95, 117, 255, 0.95) 0%, rgba(151, 71, 255, 0.98) 53%, rgba(235, 51, 102, 0.96) 100%)";

const ZERO_PORTFOLIO_METRICS = {
  averageCost: 0,
  netInvested: 0,
  unrealizedPnL: 0,
  portfolioDiversity: 0,
  totalPnL: 0,
  pnlPercentage: 0,
};

const formatMarketPrice = (value, currencySymbol = "$") => {
  if (value == null) {
    return "-";
  }

  const numericValue = parseNumericValue(value);

  return `${currencySymbol}${numericValue.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatPercentValue = (
  value,
  { decimals = 2, includePlus = false } = {}
) => {
  if (value == null) {
    return "-";
  }

  const numericValue = parseNumericValue(value);
  const signPrefix = numericValue > 0 && includePlus ? "+" : "";

  return `${signPrefix}${numericValue.toFixed(decimals)}%`;
};

const formatSupplyValue = (value, symbol) => {
  if (value == null) {
    return "-";
  }

  return formatSupply(value, symbol);
};

const formatMarketCurrencyValue = (value, formatter, currencySymbol = "$") => {
  if (value == null) {
    return "-";
  }

  return formatter(value, currencySymbol);
};

const formatPopulatedMetricCurrency = (value) => {
  const numericValue = parseNumericValue(value);

  return `R${numericValue.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatPopulatedSignedMetricCurrency = (value) => {
  const numericValue = parseNumericValue(value);
  const prefix = numericValue < 0 ? "-" : numericValue > 0 ? "+" : "";

  return `${prefix}${formatPopulatedMetricCurrency(Math.abs(numericValue))}`;
};

function ManageIcon() {
  return (
    <span className="relative block h-6 w-6">
      <img
        src={MANAGE_ICON_PART}
        alt=""
        aria-hidden="true"
        className="absolute left-1/2 top-1/2 h-[17px] w-[1px] -translate-x-1/2 -translate-y-1/2"
      />
      <img
        src={MANAGE_ICON_PART}
        alt=""
        aria-hidden="true"
        className="absolute left-1/2 top-1/2 h-[17px] w-[1px] -translate-x-1/2 -translate-y-1/2 rotate-90"
      />
    </span>
  );
}

function YieldActionButton({
  icon,
  label,
  disabled,
  onClick,
  iconClassName = "h-5 w-5",
}) {
  return (
    <button
      type="button"
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={disabled ? undefined : onClick}
      className={cn(
        "flex h-[72px] items-center justify-between rounded-2xl border border-[#ffffff54] p-4 text-left text-white backdrop-blur-[16px] transition-all",
        disabled ? "cursor-not-allowed" : "hover:text-white/50"
      )}
      style={{
        background:
          "radial-gradient(196.29% 458.78% at -8.86% -12.86%, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 100%)",
      }}
    >
      <div className="flex min-w-0 flex-1 items-center gap-4">
        {typeof icon === "string" ? (
          <img
            src={icon}
            alt=""
            aria-hidden="true"
            className={cn("object-contain", iconClassName)}
          />
        ) : (
          icon
        )}
        <span className="w-[202px] text-base font-semibold leading-6">
          {label}
        </span>
      </div>
      <span className="flex h-10 w-10 items-center justify-center rounded-full border border-accent/30 bg-transparent">
        <img
          src={CTA_ARROW_ICON}
          alt=""
          aria-hidden="true"
          className="h-3 w-3"
        />
      </span>
    </button>
  );
}

function EmptyStatePillButton({ label, onClick }) {
  return (
    <button
      type="button"
      className="flex h-12 w-[149.5px] items-center justify-center rounded-full border border-[#EBE9EF] bg-white text-sm font-semibold leading-5 text-[#6D6584] shadow-none transition-colors hover:bg-[#FAF9FC]"
      onClick={onClick}
    >
      {label}
    </button>
  );
}

function MarketMetric({ label, value, emphasize = false }) {
  return (
    <div className="w-[123px] space-y-[10px]">
      <p className="text-xs font-normal text-[#6D6584]">{label}</p>
      <p
        className={cn(
          "text-base font-semibold leading-6 text-[#423E51]",
          emphasize && "text-[20px] text-[#17C3A5]"
        )}
      >
        {value}
      </p>
    </div>
  );
}

function SparkleIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-[14px] w-[14px]"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M7 0.75L8.403 5.097L12.75 6.5L8.403 7.903L7 12.25L5.597 7.903L1.25 6.5L5.597 5.097L7 0.75Z"
        fill="url(#sparkleGradient)"
      />
      <defs>
        <linearGradient
          id="sparkleGradient"
          x1="1.25"
          y1="6.5"
          x2="12.75"
          y2="6.5"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#9747FF" />
          <stop offset="1" stopColor="#EB3366" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function UnlockIllustration() {
  return (
    <div className="relative h-[136px] w-[194px]">
      <span className="absolute left-[19px] top-[10px] h-[48px] w-[48px] rounded-full bg-[#F2E9FB]" />
      <span className="absolute right-[28px] top-[18px] h-[16px] w-[16px] rounded-full bg-[#EB3366]" />
      <img
        src={UNLOCK_ILLUSTRATION}
        alt=""
        aria-hidden="true"
        className="absolute left-[46px] top-[16px] h-[103px] w-[103px] object-contain"
      />
    </div>
  );
}

function HistoryIllustration() {
  return (
    <div className="relative h-[91px] w-[130px]">
      <span className="absolute bottom-[2px] left-[5px] h-[26px] w-[26px] rounded-full bg-[#F2E9FB]" />
      <span className="absolute right-[29px] top-[31px] h-[6px] w-[6px] rounded-full bg-[#00FFFF]" />
      <span className="absolute right-[22px] top-[2px]">
        <SparkleIcon />
      </span>
      <img
        src={HISTORY_ILLUSTRATION}
        alt=""
        aria-hidden="true"
        className="absolute left-[21px] top-[1px] h-[89px] w-[89px] object-contain"
      />
    </div>
  );
}

function getPerformanceToneClass(value) {
  if (value > 0) {
    return "text-[#17C3A5]";
  }

  if (value < 0) {
    return "text-[#FFF2EB]";
  }

  return "text-[#EBE9EF]";
}

function getInlinePerformanceToneClass(value) {
  if (value > 0) {
    return "text-[#17C3A5]";
  }

  if (value < 0) {
    return "text-[#DA2F58]";
  }

  return "text-[#6D6584]";
}

function YieldMetricCard({ label, value, valueClassName }) {
  return (
    <div
      className="flex h-[112px] flex-col justify-start rounded-[20px] border border-[rgba(255,255,255,0.32)] px-[25px] pt-[30px] backdrop-blur-[9.4px]"
      style={{
        background: "rgba(255,255,255,0.11)",
      }}
    >
      <div className="flex flex-col items-start gap-[9px]">
        <p className="w-full text-base leading-6 text-[#EBE9EF]">{label}</p>
        <p
          className={cn(
            "w-full text-[24px] font-semibold leading-7 text-[#EBE9EF]",
            valueClassName
          )}
        >
          {value}
        </p>
      </div>
    </div>
  );
}

function HistoryStatusBadge({ status }) {
  const normalizedStatus = (status || "").toLowerCase();

  if (normalizedStatus === "pending") {
    return (
      <span className="rounded-full bg-[#FFF2EB] px-3 py-1 text-xs font-semibold uppercase tracking-[0.04em] text-[#D97706]">
        Pending
      </span>
    );
  }

  if (normalizedStatus === "rejected" || normalizedStatus === "inactive") {
    return (
      <span className="rounded-full bg-[#FDECEC] px-3 py-1 text-xs font-semibold uppercase tracking-[0.04em] text-[#DA2F58]">
        {normalizedStatus === "inactive" ? "Inactive" : "Rejected"}
      </span>
    );
  }

  if (normalizedStatus === "success" || normalizedStatus === "successful") {
    return (
      <span className="rounded-full bg-[#EEF8F6] px-3 py-1 text-xs font-semibold uppercase tracking-[0.04em] text-[#17C3A5]">
        Success
      </span>
    );
  }

  return (
    <span className="rounded-full bg-[#EEF8F6] px-3 py-1 text-xs font-semibold uppercase tracking-[0.04em] text-[#17C3A5]">
      Active
    </span>
  );
}

function YieldHistoryRow({ entry }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-[20px] border border-[#F1EDF7] px-4 py-4 transition-colors hover:bg-[#FAF9FC]">
      <div className="min-w-0">
        <p className="truncate text-base font-semibold leading-6 text-[#33125F]">
          {entry.title}
        </p>
        <p className="mt-1 text-sm leading-5 text-[#6D6584]">
          {entry.subtitle}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <HistoryStatusBadge status={entry.status} />
          <span className="text-xs font-medium text-[#9D96AF]">
            {entry.dateLabel}
          </span>
        </div>
      </div>
      <div className="shrink-0 text-right">
        <p className="text-base font-semibold leading-6 text-[#423E51]">
          {entry.amountLabel}
        </p>
        {entry.valueLabel ? (
          <p className="mt-1 text-xs font-medium text-[#6D6584]">
            {entry.valueLabel}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function EmptyHistoryState({ onStart }) {
  return (
    <div className="mx-auto flex min-h-[672px] max-w-[361px] flex-col items-center justify-center gap-[23px] text-center xl:h-full xl:min-h-0">
      <HistoryIllustration />

      <h3 className="text-2xl font-semibold leading-7 text-[#33125F]">
        No history
      </h3>
      <p className="max-w-[316px] text-sm leading-5 text-[#423E51]">
        Your lock and unlock activity will appear here once you start using
        Yield.
      </p>
      <div className="flex items-center gap-4">
        <span className="h-3 w-3 rounded-full bg-[#EBE9EF]" />
        <span className="h-3 w-12 rounded-full bg-[#A263E4]" />
      </div>
      <EmptyStatePillButton label="Start" onClick={onStart} />
    </div>
  );
}

export default function InvestYieldState({
  variant,
  totalYieldValue,
  ethBalance,
  ethAsset,
  assetBalance = ethBalance,
  asset = ethAsset,
  assetSymbol = "ZAR8",
  assetName = "ZAR8",
  marketData,
  portfolioMetrics = ZERO_PORTFOLIO_METRICS,
  historyEntries = [],
  onManage,
  onCalculator,
  onStart,
  isPreview = false,
}) {
  const navigate = useNavigate();
  const normalizedAssetBalance = parseNumericValue(assetBalance);
  const hasAssetBalance = normalizedAssetBalance > 0;
  const normalizedAssetSymbol = assetSymbol.toUpperCase();
  const marketCurrencySymbol = normalizedAssetSymbol === "ZAR8" ? "R" : "$";
  const totalZarDisplay = formatFiatWithTwoDecimals(totalYieldValue);
  const resolvedMarketData = marketData ?? EMPTY_YIELD_MARKET_DATA;
  const description = marketData?.description || FALLBACK_DESCRIPTION;
  const isPopulated = variant === "populated";
  const safeMetrics = {
    ...ZERO_PORTFOLIO_METRICS,
    ...(portfolioMetrics || {}),
  };

  const balanceDisplay = hasAssetBalance
    ? formatWithSignificantDigits(normalizedAssetBalance, {
        minSignificant: 4,
        maxDecimals: 8,
      })
    : "0.00";
  const populatedBalanceDisplay = hasAssetBalance
    ? `${normalizedAssetSymbol} ${balanceDisplay}`
    : `${normalizedAssetSymbol} 0.00`;
  const assetSelection =
    asset && asset.asset
      ? {
          asset: asset.asset,
          symbol: asset.asset?.toUpperCase(),
          assetName: asset.assetName || assetName,
          image: asset.assetIcon || asset.image || "",
        }
      : null;

  const roiSource =
    resolvedMarketData?.roiPercentage != null
      ? resolvedMarketData.roiPercentage
      : resolvedMarketData?.percentChange30d;
  const roiValue = roiSource == null ? null : parseNumericValue(roiSource);
  const roiDisplay =
    roiValue == null
      ? "-"
      : formatPercentValue(roiValue, {
          decimals: 1,
          includePlus: true,
        });
  const currentMarketPrice = parseNumericValue(
    resolvedMarketData?.priceInZar ?? resolvedMarketData?.price
  );
  const allTimeHigh = parseNumericValue(resolvedMarketData?.allTimeHigh);
  const athDisplay =
    currentMarketPrice > 0 && allTimeHigh > 0
      ? formatPercentOfATH(currentMarketPrice, allTimeHigh)
      : resolvedMarketData?.athPercentage != null
        ? formatPercentValue(resolvedMarketData.athPercentage)
        : "-";

  const marketMetrics = [
    {
      label: "Market Cap",
      value: formatMarketCurrencyValue(
        resolvedMarketData.marketCap,
        formatMarketCap,
        marketCurrencySymbol
      ),
    },
    {
      label: "24h Volume",
      value: formatMarketCurrencyValue(
        resolvedMarketData.volume24h,
        formatVolume,
        marketCurrencySymbol
      ),
    },
    {
      label: "Circulating",
      value: formatSupplyValue(
        resolvedMarketData.circulatingSupply,
        normalizedAssetSymbol
      ),
    },
    {
      label: "Rank",
      value:
        resolvedMarketData?.rank != null ? `#${resolvedMarketData.rank}` : "-",
    },
    {
      label: "Max Supply",
      value: formatSupplyValue(
        resolvedMarketData.maxSupply,
        normalizedAssetSymbol
      ),
    },
    {
      label: "Total Supply",
      value: formatSupplyValue(
        resolvedMarketData.totalSupply,
        normalizedAssetSymbol
      ),
    },
    {
      label: "All-Time High",
      value: formatMarketPrice(
        resolvedMarketData?.allTimeHigh,
        marketCurrencySymbol
      ),
    },
    {
      label: "% of ATH",
      value: athDisplay,
    },
  ];

  const pnlPrefix =
    safeMetrics.totalPnL > 0 ? "+" : safeMetrics.totalPnL < 0 ? "-" : "";
  const absolutePnlDisplay = formatFiatWithTwoDecimals(
    Math.abs(safeMetrics.totalPnL || 0)
  ).replace(/^R /, "");
  const pnlSummaryAmount = `${pnlPrefix ? `${pnlPrefix} ` : ""}R ${absolutePnlDisplay}`;
  const pnlPercentageDisplay = Number.isInteger(safeMetrics.pnlPercentage || 0)
    ? `${safeMetrics.pnlPercentage || 0}%`
    : formatPercentValue(safeMetrics.pnlPercentage || 0);
  const pnlSummary = `${pnlSummaryAmount} (${pnlPercentageDisplay})`;
  const topMarketMetrics = marketMetrics.slice(0, 4);
  const bottomMarketMetrics = marketMetrics.slice(4);
  const hasHistory = historyEntries.length > 0;

  const contentWidthClass = isPreview ? "w-[1304px]" : "w-full";
  const outerPaddingClass = isPreview
    ? "w-[1304px]"
    : "px-5 pb-12 pt-3 md:px-8 xl:px-[52px]";

  return (
    <div className={outerPaddingClass}>
      <div className={cn("flex flex-col gap-[37px]", contentWidthClass)}>
        <div
          className={cn(
            "grid gap-3",
            isPreview
              ? "xl:grid-cols-[752px_540px]"
              : "xl:grid-cols-[minmax(0,1.45fr)_minmax(420px,1fr)]"
          )}
        >
          <div
            className={cn(
              "relative min-h-[327px] overflow-hidden rounded-[20px]",
              isPopulated ? "text-white" : "text-[#33125F]"
            )}
            style={{
              backgroundColor: "#FFFFFF",
              backgroundImage: isPopulated ? HERO_BASE_GRADIENT : "none",
            }}
          >
            <img
              src={HERO_BACKGROUND}
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute left-[-120px] top-[-55px] max-w-none"
              style={{
                width: "1803.565px",
                height: "1379.698px",
              }}
            />
            <div
              className="absolute inset-0 mix-blend-overlay opacity-40"
              style={{
                backgroundImage: `url(${NOISE_TEXTURE})`,
                backgroundPosition: "top left",
                backgroundSize: "1697.34px 846.68px",
              }}
            />
            <div className="relative flex min-h-[327px] flex-col px-5 pb-5 pt-[34px] xl:block">
              <p
                className={cn(
                  "text-2xl font-semibold leading-7 xl:absolute xl:left-5 xl:top-[34px]",
                  isPopulated ? "text-[#F1EDF7]" : "text-[#33125F]"
                )}
              >
                ZAR8/BTC Yield
              </p>
              <div className="mt-[67px] space-y-[13px] xl:absolute xl:left-[23px] xl:top-[137px] xl:mt-0 xl:w-[358px]">
                <p className="text-base leading-6 text-[rgba(235,233,239,0.6)]">
                  Total ZAR8
                </p>
                <p className="text-[48px] font-semibold leading-[40px] text-white">
                  {totalZarDisplay}
                </p>
              </div>

              <div className="mt-10 grid gap-[14px] lg:grid-cols-3 xl:absolute xl:bottom-5 xl:left-[23px] xl:right-[23px] xl:mt-0">
                <YieldActionButton
                  icon={<ManageIcon />}
                  label="Manage"
                  disabled={false}
                  onClick={() => {
                    if (onManage) {
                      onManage();
                      return;
                    }

                    navigate("/trading/digitalWallet");
                  }}
                />
                <YieldActionButton
                  icon={TRACK_ICON}
                  label="Track"
                  disabled={isPreview}
                  onClick={() => navigate("/zar8/track")}
                  iconClassName="h-8 w-8"
                />
                <YieldActionButton
                  icon={CALCULATOR_ICON}
                  label="Calculator"
                  disabled={isPreview}
                  onClick={() => {
                    if (onCalculator) {
                      onCalculator();
                      return;
                    }

                    navigate("/zar8/calculator");
                  }}
                  iconClassName="h-8 w-8"
                />
              </div>
            </div>
          </div>

          <div
            className={cn(
              "relative rounded-[20px] border border-[#F1EDF7] bg-white px-6 py-8 xl:h-[327px] xl:overflow-hidden",
              isPreview ? "xl:w-[540px]" : "xl:w-auto"
            )}
          >
            <div className="flex h-full flex-col items-center justify-center text-center xl:block">
              <div className="xl:absolute xl:left-1/2 xl:top-8 xl:-translate-x-1/2">
                <UnlockIllustration />
              </div>
              <div className="mt-[18px] xl:absolute xl:left-1/2 xl:top-[186px] xl:flex xl:w-[556px] xl:-translate-x-1/2 xl:flex-col xl:items-center xl:gap-6 xl:mt-0">
                <h2 className="text-2xl font-semibold leading-7 text-[#57249C]">
                  Unlock the Potential of Your ZAR8
                </h2>
                <p className="mt-6 max-w-[480px] text-sm leading-5 text-[#423E51] xl:mt-0">
                  Secure your ZAR8 for a chosen period and automatically earn
                  daily Bitcoin yield. Simple, secure, and rewarding.
                </p>
              </div>
              <div className="mt-5 flex items-center gap-[10px] xl:absolute xl:left-1/2 xl:top-[287px] xl:-translate-x-1/2 xl:mt-0">
                <span className="h-[7px] w-[30px] rounded-full bg-[#A263E4]" />
                <span className="h-[7px] w-[7px] rounded-full bg-[#EBE9EF]" />
              </div>
            </div>
          </div>
        </div>

        <div
          className={cn(
            "grid gap-[19px]",
            isPreview
              ? "xl:grid-cols-[616px_669px]"
              : "xl:grid-cols-[minmax(0,1fr)_minmax(0,1.08fr)]"
          )}
        >
          <div className="flex flex-col rounded-[24px] border border-[#EBE9EF] bg-white px-[30px] pb-8 pt-4 xl:h-[270px]">
            <div className="flex h-[47px] shrink-0 items-center">
              <p className="text-xl font-semibold leading-7 text-[#6D6584]">
                Description
              </p>
            </div>
            <p className="mt-1 w-full flex-1 text-base leading-6 text-[#423E51]">
              {description}
            </p>
            <img
              src={coinMarketCap}
              className="mt-4 h-[35px] w-[119px]"
              alt="CoinMarketCap"
            />
          </div>

          <div
            className={cn(
              "flex flex-col rounded-[24px] border border-[#F1EDF7] bg-white px-[28px] py-[26px] xl:h-[270px]",
              isPreview ? "xl:w-[669px]" : "xl:w-auto"
            )}
          >
            <p className="text-xl font-semibold leading-7 text-[#6D6584]">
              Market Data
            </p>
            <div className="mt-[22px] flex flex-wrap gap-9">
              {topMarketMetrics.map((metric) => (
                <MarketMetric
                  key={metric.label}
                  label={metric.label}
                  value={metric.value}
                />
              ))}
            </div>
            <div className="mt-[10px] flex flex-wrap gap-9">
              {bottomMarketMetrics.map((metric) => (
                <MarketMetric
                  key={metric.label}
                  label={metric.label}
                  value={metric.value}
                />
              ))}
            </div>
            <div className="mt-[10px] flex">
              <MarketMetric label="ROI" value={roiDisplay} emphasize />
            </div>
          </div>
        </div>

        <div
          className={cn(
            "grid gap-[26px]",
            isPreview
              ? "xl:grid-cols-[440px_838px]"
              : "xl:grid-cols-[440px_minmax(0,1fr)]"
          )}
        >
          <div
            className="relative overflow-hidden rounded-[24px] border border-[#EBE9EF] bg-white xl:h-[672px]"
            style={
              isPopulated
                ? {
                    backgroundImage: BALANCE_BASE_GRADIENT,
                  }
                : undefined
            }
          >
            <img
              src={BALANCE_BACKGROUND}
              alt=""
              aria-hidden="true"
              className={cn(
                "pointer-events-none absolute max-w-none",
                isPopulated
                  ? "left-[-167px] top-[473px]"
                  : "left-[-166px] top-[474px]"
              )}
              style={{
                width: "760.727px",
                height: "581.944px",
              }}
            />
            <div
              className={cn(
                "mix-blend-overlay opacity-40",
                isPopulated
                  ? "absolute inset-0"
                  : "absolute left-[-12px] top-0 h-[672px] w-[452px]"
              )}
              style={{
                backgroundImage: `url(${NOISE_TEXTURE})`,
                backgroundPosition: "top left",
                backgroundSize: "1697.34px 846.68px",
              }}
            />

            {isPopulated ? (
              <div className="relative flex min-h-[672px] flex-col px-[29px] pb-8 pt-4 text-white xl:px-0 xl:pb-0 xl:pt-0">
                <div className="text-center xl:absolute xl:left-[29px] xl:top-[15px] xl:flex xl:h-[112px] xl:w-[380px] xl:flex-col xl:items-center xl:justify-center xl:rounded-[20px] xl:px-[5px] xl:py-[25px]">
                  <p className="text-xl font-semibold leading-7 text-[#EBE9EF]">
                    My Balance
                  </p>
                  <p className="mt-[5px] text-[32px] font-semibold leading-10 text-[#EBE9EF]">
                    {populatedBalanceDisplay}
                  </p>
                  <p
                    className={cn(
                      "mt-[5px] text-sm font-semibold leading-5",
                      getInlinePerformanceToneClass(safeMetrics.totalPnL || 0)
                    )}
                  >
                    {pnlSummary}
                  </p>
                </div>

                <div className="mt-8 grid gap-4 xl:absolute xl:bottom-[33px] xl:left-[29px] xl:w-[380px] xl:gap-4">
                  <YieldMetricCard
                    label="Average cost"
                    value={formatPopulatedMetricCurrency(
                      safeMetrics.averageCost
                    )}
                  />
                  <YieldMetricCard
                    label="Net Invested"
                    value={formatPopulatedMetricCurrency(
                      safeMetrics.netInvested
                    )}
                  />
                  <YieldMetricCard
                    label="Unrealized Profit & Loss"
                    value={formatPopulatedSignedMetricCurrency(
                      safeMetrics.unrealizedPnL
                    )}
                    valueClassName={getPerformanceToneClass(
                      safeMetrics.unrealizedPnL
                    )}
                  />
                  <YieldMetricCard
                    label="Portfolio Diversity"
                    value={formatPercentValue(safeMetrics.portfolioDiversity)}
                  />
                </div>
              </div>
            ) : (
              <div className="relative flex min-h-[672px] flex-col items-center px-[30px] pb-7 pt-[29px] text-center xl:block">
                <div className="space-y-[6px] xl:absolute xl:left-[30px] xl:top-[29px] xl:flex xl:h-[96px] xl:w-[380px] xl:flex-col xl:items-center xl:justify-center xl:px-[5px] xl:py-[25px]">
                  <p className="text-base leading-6 text-[#6D6584]">
                    My Balance
                  </p>
                  <p className="text-[32px] font-semibold leading-10 text-[#423E51]">
                    {balanceDisplay}
                  </p>
                  {hasAssetBalance && (
                    <p className="text-sm font-medium text-[#6D6584]">
                      {normalizedAssetSymbol}
                    </p>
                  )}
                </div>

                <div className="mt-28 flex flex-1 flex-col items-center justify-center gap-8 xl:absolute xl:left-[30px] xl:top-[112px] xl:mt-0 xl:h-[432px] xl:w-[380px]">
                  <h3 className="text-2xl font-semibold leading-7 text-[#33125F]">
                    {hasAssetBalance ? (
                      <>
                        {normalizedAssetSymbol} Ready
                        <br />
                        for Yield
                      </>
                    ) : (
                      <>
                        No {normalizedAssetSymbol}
                        <br />
                        Balance Available
                      </>
                    )}
                  </h3>
                  <EmptyStatePillButton
                    label={
                      hasAssetBalance
                        ? `Manage ${normalizedAssetSymbol}`
                        : `Buy ${normalizedAssetSymbol}`
                    }
                    onClick={() => {
                      navigate("/trading/digitalWallet", {
                        state: {
                          tabActive: "Buy",
                          selectedTab: "Buy",
                          selectedCrypto: assetSelection,
                          type: "buy-sell",
                        },
                      });
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          <div
            className={cn(
              "rounded-[24px] border border-[#EBE9EF] bg-white px-6 py-7 xl:h-[672px] xl:overflow-hidden",
              isPreview ? "xl:w-[838px]" : "xl:w-auto",
              isPopulated
                ? "xl:px-[27px] xl:py-[26px]"
                : "xl:pb-4 xl:pl-[7px] xl:pr-6 xl:pt-[11px]"
            )}
          >
            {isPopulated && hasHistory ? (
              <div className="flex min-h-[672px] flex-col">
                <div>
                  <div>
                    <h3 className="text-2xl font-semibold leading-7 text-[#33125F]">
                      History
                    </h3>
                    <p className="mt-2 text-sm leading-5 text-[#6D6584]">
                      Latest lock and unlock activity on your Yield positions.
                    </p>
                  </div>
                </div>
                <div className="mt-8 grid gap-4">
                  {historyEntries.map((entry) => (
                    <YieldHistoryRow key={entry.id} entry={entry} />
                  ))}
                </div>
              </div>
            ) : (
              <EmptyHistoryState onStart={onStart} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
